import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, Modal, PillTabs } from '../../../school_panel/components/ui';
import { FaTimes } from '../../../school_panel/components/ui/icons';
import { SchoolApi } from '../../../school_panel/api/client';
import { useCreateNotice, useSectionsList } from '../../../school_panel/api/hooks';
import type { CreateNoticeBody } from '../../../school_panel/api/types';
import AttachmentsField from '../../../school_panel/components/AttachmentsField';
import IndividualStudentPicker from '../../../components/Exams/IndividualStudentPicker';

export type NoticeMode = 'school' | 'grade' | 'sections' | 'students';
export interface NoticeDraft {
  title: string; body: string; mode: NoticeMode;
  gradeId: number | null; sectionIds: number[]; studentIds: number[];
}

export function noticePayload(d: NoticeDraft): CreateNoticeBody {
  const base = { title: d.title.trim(), body: d.body.trim() };
  if (d.mode === 'school') return { ...base, audience: 'grade' };
  if (d.mode === 'grade') return { ...base, audience: 'grade', grade: d.gradeId! };
  if (d.mode === 'sections') return { ...base, audience: 'sections', grade: d.gradeId!, section_ids: d.sectionIds };
  return { ...base, audience: 'students', grade: d.gradeId!, student_ids: d.studentIds };
}

export function noticeError(d: NoticeDraft): string | null {
  if (!d.title.trim()) return 'Title is required.';
  if (!d.body.trim()) return 'Message is required.';
  if (d.mode !== 'school' && d.gradeId == null) return 'Pick a grade.';
  if (d.mode === 'sections' && d.sectionIds.length === 0) return 'Pick at least one section.';
  if (d.mode === 'students' && d.studentIds.length === 0) return 'Pick at least one student.';
  return null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  initial?: Partial<NoticeDraft>;
  onSent?: () => void;
  /** Render as an in-page card (School tasks) instead of a modal. */
  inline?: boolean;
}

const FIELD_KEYS = ['title', 'body', 'grade', 'section_ids', 'student_ids'] as const;
type FieldKey = typeof FIELD_KEYS[number];

function blank(isAdmin: boolean, initial?: Partial<NoticeDraft>): NoticeDraft {
  const d: NoticeDraft = {
    title: '', body: '', mode: isAdmin ? 'school' : 'grade',
    gradeId: null, sectionIds: [], studentIds: [], ...initial,
  };
  if (!isAdmin && d.mode === 'school') d.mode = 'grade';
  return d;
}

const fieldClass = 'w-full rounded-[12px] border border-line-strong bg-surface min-h-[44px] px-3.5 py-3 text-sm text-ink outline-none placeholder:text-muted focus:ring-2 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-60';
const labelClass = 'mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

function ErrorText({ children }: { children?: string }) {
  if (!children) return null;
  return <p className="mt-1 text-xs text-danger-ink">{children}</p>;
}

export default function NoticeComposer({ isOpen, onClose, isAdmin, initial, onSent, inline = false }: Props) {
  const [draft, setDraft] = useState<NoticeDraft>(() => blank(isAdmin, initial));
  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [pending, setPending] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const create = useCreateNotice();
  const cardRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (isOpen) {
      setDraft(blank(isAdmin, initial));
      setTopError(null);
      setFieldErrors({});
      setPending([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!inline || !isOpen) return;
    const el = cardRef.current;
    el?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    el?.querySelector<HTMLInputElement>('#notice-title')?.focus();
  }, [inline, isOpen]);
  useEffect(() => {
    if (!inline || !isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [inline, isOpen, onClose]);

  const gradesQuery = useQuery({
    queryKey: ['analytics.grades'],
    queryFn: () => SchoolApi.analyticsGrades(),
    enabled: isOpen,
  });
  const grades = gradesQuery.data?.grades ?? [];
  // A grade that is not (or no longer) in the list counts as no grade picked.
  const known = gradesQuery.data != null;
  const gradeId = gradesQuery.isError || (known && draft.gradeId != null && !grades.some((g) => g.id === draft.gradeId)) ? null : draft.gradeId;
  // Send waits for the grades list so only a validated grade id is ever sent.
  const waitingForGrades = draft.mode !== 'school' && gradesQuery.isPending;
  const sectionsQuery = useSectionsList(isOpen && draft.mode === 'sections' ? gradeId : null);
  // A teacher reaches only their own classes: the sections the grades list gives them
  // (the server refuses anyone else), and the whole grade only when all of it is theirs.
  const gradeRow = grades.find((g) => g.id === gradeId);
  const sections = (sectionsQuery.data?.sections ?? [])
    .filter((s) => isAdmin || !gradeRow || gradeRow.sections.some((x) => x.id === s.id));
  const wholeGradeOk = isAdmin || gradeRow?.whole_grade !== false;
  useEffect(() => {
    if (!wholeGradeOk && draft.mode === 'grade') setDraft((d) => ({ ...d, mode: 'sections' }));
  }, [wholeGradeOk, draft.mode]);

  const set = (patch: Partial<NoticeDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const toggle = (key: 'sectionIds' | 'studentIds', id: number) =>
    setDraft((d) => ({ ...d, [key]: d[key].includes(id) ? d[key].filter((x) => x !== id) : [...d[key], id] }));

  const modes: { value: NoticeMode; label: string }[] = [
    ...(isAdmin ? [{ value: 'school' as const, label: 'Whole school' }] : []),
    ...(wholeGradeOk ? [{ value: 'grade' as const, label: 'Grade' }] : []),
    { value: 'sections', label: wholeGradeOk ? 'Some sections' : 'My sections' },
    { value: 'students', label: 'Specific students' },
  ];

  // Audience summary (design `modeSummary`): "Sending to Grade 10 · A, B · 72 students". Counts come
  // from the grades list the composer already loads; a count is left out when the list has none.
  const count = (n: number | null) => (n == null ? '' : ` · ${n} ${n === 1 ? 'student' : 'students'}`);
  let summary = '';
  if (draft.mode === 'school') {
    summary = `Sending to the whole school${known ? count(grades.reduce((n, g) => n + g.student_count, 0)) : ''}`;
  } else if (draft.mode === 'grade') {
    const g = grades.find((x) => x.id === gradeId);
    if (g) summary = `Sending to ${/^grade/i.test(g.name) ? g.name : `Grade ${g.name}`}${count(g.student_count)}`;
  } else if (draft.mode === 'sections') {
    const g = grades.find((x) => x.id === gradeId);
    const picked = sections.filter((s) => draft.sectionIds.includes(s.id));
    if (picked.length) {
      const tally = g ? picked.map((p) => g.sections.find((x) => x.id === p.id)?.student_count) : [];
      const total = g && tally.length === picked.length && tally.every((n) => n != null) ? (tally as number[]).reduce((a, b) => a + b, 0) : null;
      const names = g ? picked.map((p) => g.sections.find((x) => x.id === p.id)?.name ?? p.display).join(', ') : picked.map((p) => p.display).join(', ');
      const gName = g ? (/^grade/i.test(g.name) ? g.name : `Grade ${g.name}`) : null;
      summary = `Sending to ${gName ? `${gName} · ` : ''}${names}${count(total)}`;
    }
  } else if (draft.studentIds.length) {
    const n = draft.studentIds.length;
    summary = `Sending to ${n} student${n === 1 ? '' : 's'}`;
  }

  async function submit() {
    if (waitingForGrades) return;
    setTopError(null);
    setFieldErrors({});
    const sendDraft = { ...draft, gradeId };
    const err = noticeError(sendDraft);
    if (err) { setTopError(err); return; }
    try {
      const task = await create.mutateAsync(noticePayload(sendDraft));
      if (pending.length) {
        setUploading(true);
        try {
          await SchoolApi.uploadTaskAttachments(task.id, pending);
          toast.success(`Notice sent with ${pending.length} file(s)`);
        } catch {
          toast.error("Notice sent, but the files couldn't be uploaded. Open the notice to try again.");
        } finally {
          setUploading(false);
        }
      }
      setPending([]);
      onSent?.();
      onClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = (e as any)?.response?.data;
      if (data && data.code === 'out_of_scope') {
        setTopError("Some of these students aren't in your classes.");
        return;
      }
      const found: Partial<Record<FieldKey, string>> = {};
      if (data && typeof data === 'object') {
        for (const k of FIELD_KEYS) {
          if (data[k] != null) found[k] = Array.isArray(data[k]) ? data[k].join(' ') : String(data[k]);
        }
      }
      if (Object.keys(found).length) {
        const shown: FieldKey[] = ['title', 'body'];
        if (draft.mode !== 'school') shown.push('grade');
        if (draft.mode === 'sections') shown.push('section_ids');
        if (draft.mode === 'students') shown.push('student_ids');
        const hidden = (Object.keys(found) as FieldKey[]).filter((k) => !shown.includes(k));
        setFieldErrors(found);
        if (hidden.length) setTopError(hidden.map((k) => found[k]).join(' '));
      } else setTopError('Could not send the notice. Try again.');
    }
  }

  const footer = (
    <>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button variant="secondary" onClick={submit} loading={create.isPending || uploading} disabled={waitingForGrades}>Send notice</Button>
    </>
  );
  const alertEl = topError ? <p role="alert" className="text-sm text-danger-ink">{topError}</p> : null;
  const leftFields = (
    <>

        <div>
          <label htmlFor="notice-title" className={labelClass}>Title</label>
          <input id="notice-title" type="text" maxLength={200} className={fieldClass}
            value={draft.title} onChange={(e) => set({ title: e.target.value })} />
          <ErrorText>{fieldErrors.title}</ErrorText>
        </div>

        <div>
          <label htmlFor="notice-body" className={labelClass}>Message</label>
          <textarea id="notice-body" rows={4} maxLength={2000} className={fieldClass}
            value={draft.body} onChange={(e) => set({ body: e.target.value })} />
          <div className="mt-1 text-right font-mono text-xs text-muted">{draft.body.length} / 2000</div>
          <ErrorText>{fieldErrors.body}</ErrorText>
        </div>

        <div>
          <span className={labelClass}>Attachments</span>
          <AttachmentsField attachments={[]} pending={pending} disabled={create.isPending || uploading}
            onAdd={(files) => setPending((p) => [...p, ...files])}
            onRemove={(item) => setPending((p) => p.filter((f) => f !== item))} />
        </div>
    </>
  );
  const rightFields = (
    <>
        <div>
          <span className={labelClass}>Send to</span>
          <PillTabs label="Audience" scroll value={draft.mode} onChange={(mode) => set({ mode })}
            options={modes.map((m) => ({ key: m.value, label: m.label }))} />
        </div>

        {draft.mode !== 'school' && (
          <div>
            <span className={labelClass}>Which grade</span>
            <PillTabs label="Which grade" scroll value={gradeId == null ? '' : String(gradeId)}
              onChange={(k) => set({ gradeId: k ? Number(k) : null, sectionIds: [], studentIds: [] })}
              options={grades.map((g) => ({ key: String(g.id), label: g.name }))} />
            <ErrorText>{fieldErrors.grade}</ErrorText>
          </div>
        )}

        {draft.mode === 'sections' && gradeId != null && (
          <div>
            <div className="max-h-48 overflow-y-auto rounded-[12px] border border-line-strong">
              {sections.map((s) => (
                <label key={s.id} className="flex items-center gap-2 border-b border-line px-3 py-2 text-sm text-ink last:border-0">
                  <input type="checkbox" className="h-4 w-4 accent-navy" checked={draft.sectionIds.includes(s.id)}
                    onChange={() => toggle('sectionIds', s.id)} />
                  {s.display}
                </label>
              ))}
            </div>
            <ErrorText>{fieldErrors.section_ids}</ErrorText>
          </div>
        )}

        {draft.mode === 'students' && (
          <div>
            <IndividualStudentPicker gradeId={gradeId == null ? '' : String(gradeId)}
              selectedIds={draft.studentIds} onToggle={(id) => toggle('studentIds', id)} />
            <ErrorText>{fieldErrors.student_ids}</ErrorText>
          </div>
        )}
    </>
  );
  const summaryEl = <p className="min-h-[40px] rounded-[12px] bg-ground px-3 py-2.5 text-[13px] text-muted">{summary}</p>;
  const body = (
    <div className="space-y-4">
      {alertEl}
      {leftFields}
      {rightFields}
      {summary && summaryEl}
    </div>
  );

  if (inline) {
    if (!isOpen) return null;
    return (
      <section ref={cardRef} aria-label="New notice" className="mb-[18px] rounded-[20px] border border-l-4 border-line border-l-accent bg-surface p-[22px] shadow-card">
        <div className="mb-[18px] flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-[17px] font-bold tracking-[-0.01em] text-ink">New notice</h2>
            <p className="mt-1 text-[13px] text-muted">Students see it in their task list; exams are assigned from the exam editor.</p>
          </div>
          <button type="button" aria-label="Close composer" onClick={onClose}
            className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-[12px] border border-line-strong bg-surface text-ink hover:bg-surface-2">
            <FaTimes size={16} aria-hidden />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-2 min-[1181px]:grid-cols-12">
          <div className="flex min-w-0 flex-col gap-[14px] min-[1181px]:col-span-7">
            {alertEl}
            {leftFields}
          </div>
          <div className="flex min-w-0 flex-col gap-[14px] min-[1181px]:col-span-5">
            {rightFields}
            {summaryEl}
            <div className="mt-auto flex justify-end gap-2.5">{footer}</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New notice" footer={footer}>
      {body}
    </Modal>
  );
}
